'use client'

import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import {
    WhatsappBroadcastFormData,
    createWhatsappBroadcast,
    getWhatsappBroadcast,
    updateWhatsappBroadcast,
} from '@/lib/api/messaging/whatsapp-broadcasts-api'
import {
    WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS,
    WhatsappTemplate,
    getWhatsappTemplates,
} from '@/lib/api/messaging/whatsapp-templates-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import { MdArrowBack, MdSave } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MetaLimitCard } from './meta-limit-card'

const HOURS = Array.from({ length: 25 }, (_, h) => h)

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <label className="flex flex-col gap-1.5 w-full">
        <span className="text-sm font-medium text-base-content/80">{label}</span>
        {children}
        {hint && <span className="text-xs text-base-content/60">{hint}</span>}
    </label>
)

const WhatsappBroadcastFormPage = () => {
    const { id } = useParams()
    const isEditing = !!id
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canCreate, canUpdate } = useResourcePermissions('messaging', 'whatsapp_broadcasts')

    const [departments, setDepartments] = useState<Array<{ id: number; name: string }>>([])
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([])
    const [started, setStarted] = useState(false)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState<WhatsappBroadcastFormData>({
        name: '',
        departmentId: 0,
        templateId: 0,
        fallbackTemplateId: null,
        nameFallback: '',
        limitSharePercent: 50,
        dailyCap: null,
        windowStartHour: 9,
        windowEndHour: 18,
        sendOnWeekends: false,
    })

    useEffect(() => {
        if (!user?.organizationId) return
        getDepartmentsByOrganization(user.organizationId, { limit: 100 })
            .then(res => {
                if (res.success && Array.isArray(res.result)) {
                    // O disparo só sai pela API oficial: o WhatsApp Web bane em volume.
                    setDepartments(
                        res.result.filter(d => d.whatsappProvider === 'WHATSAPP_API').map(d => ({ id: d.id, name: d.name })),
                    )
                }
            })
            .catch(() => {})
        getWhatsappTemplates({ active: true })
            .then(res => {
                if (res.success) {
                    setTemplates(res.result.filter(t => t.approvalStatus === 'APPROVED' || t.approvalStatus === 'PENDING'))
                }
            })
            .catch(() => {})
    }, [user?.organizationId])

    useEffect(() => {
        if (!isEditing) return
        getWhatsappBroadcast(Number(id)).then(res => {
            if (!res.success) {
                toast.error(res.error || 'Disparo não encontrado')
                return
            }
            const b = res.result
            setStarted(!!b.startedAt)
            setForm({
                name: b.name,
                departmentId: b.departmentId,
                templateId: b.templateId,
                fallbackTemplateId: b.fallbackTemplateId,
                nameFallback: b.nameFallback ?? '',
                limitSharePercent: b.limitSharePercent,
                dailyCap: b.dailyCap,
                windowStartHour: b.windowStartHour,
                windowEndHour: b.windowEndHour,
                sendOnWeekends: b.sendOnWeekends,
            })
        })
    }, [id, isEditing])

    const templateLabel = (t: WhatsappTemplate) =>
        `${t.friendlyName || t.name}${t.approvalStatus !== 'APPROVED' ? ` (${WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS[t.approvalStatus]})` : ''}`

    const selectedTemplate = useMemo(() => templates.find(t => t.id === form.templateId), [templates, form.templateId])
    const usesName = (selectedTemplate?.variables?.length ?? 0) > 0
    const nameFallbackText = (form.nameFallback ?? '').replace(/\s+/g, ' ').trim()
    // Como a mensagem chega para quem não tem nome: a variável vira o texto escolhido.
    const noNamePreview = nameFallbackText
        ? (selectedTemplate?.bodyTemplate ?? selectedTemplate?.bodyText ?? '').replace(/\{\{[^}]+\}\}/g, nameFallbackText)
        : ''

    const set = <K extends keyof WhatsappBroadcastFormData>(key: K, value: WhatsappBroadcastFormData[K]) =>
        setForm(f => ({ ...f, [key]: value }))

    const handleSubmit = async () => {
        if (!form.name.trim()) return toast.error('Informe o nome do disparo.')
        if (!form.departmentId) return toast.error('Escolha o setor remetente.')
        if (!form.templateId) return toast.error('Escolha o template.')
        if ((form.windowEndHour ?? 18) <= (form.windowStartHour ?? 9)) {
            return toast.error('O fim da janela de envio precisa ser depois do início.')
        }
        setSaving(true)
        try {
            const payload = { ...form, nameFallback: nameFallbackText || null }
            const res = isEditing ? await updateWhatsappBroadcast(Number(id), payload) : await createWhatsappBroadcast(payload)
            if (res.success) {
                toast.success(isEditing ? 'Disparo atualizado' : 'Disparo criado. Agora importe o público.')
                navigate(`/messaging/whatsapp-broadcasts/${res.result.id}`)
            } else {
                toast.error(res.error || 'Erro ao salvar')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="p-6 space-y-4 max-w-4xl">
            <div className="flex items-center gap-3">
                <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={() => navigate(-1)}>
                    <MdArrowBack size={20} />
                </button>
                <h1 className="text-2xl font-bold">{isEditing ? 'Editar disparo' : 'Novo disparo'}</h1>
            </div>

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-5 space-y-5">
                    <h2 className="font-semibold">Mensagem</h2>
                    <Field label="Nome do disparo" hint="Só para identificar na lista.">
                        <input
                            className="input input-bordered input-sm"
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            placeholder="Ex.: Retorno de atendimento - outubro"
                        />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Setor remetente" hint="Só setores na API oficial do WhatsApp.">
                            <select
                                className="select select-bordered select-sm"
                                value={form.departmentId || ''}
                                disabled={started}
                                onChange={e => set('departmentId', Number(e.target.value))}
                            >
                                <option value="">Selecione</option>
                                {departments.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Template" hint="Aprovado ou em análise; o envio só começa depois de aprovado.">
                            <select
                                className="select select-bordered select-sm"
                                value={form.templateId || ''}
                                disabled={started}
                                onChange={e => set('templateId', Number(e.target.value))}
                            >
                                <option value="">Selecione</option>
                                {templates.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {templateLabel(t)}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    {selectedTemplate && (
                        <div className="rounded-lg bg-base-200 p-3 text-sm whitespace-pre-wrap">
                            {selectedTemplate.bodyTemplate ?? selectedTemplate.bodyText}
                        </div>
                    )}
                    {usesName && (
                        <div className="rounded-lg border border-base-300 p-4 space-y-4">
                            <div>
                                <h3 className="text-sm font-semibold">Contatos sem nome</h3>
                                <p className="text-xs text-base-content/60 mt-1">
                                    O template usa o nome do contato. Para quem não tem nome, escolha um texto para
                                    ir no lugar ou um template alternativo. Sem nenhum dos dois, quem não tem nome é
                                    pulado.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field
                                    label="Texto no lugar do nome"
                                    hint={'Ex.: em "Olá, {nome}!", o texto "tudo bem" vira "Olá, tudo bem!".'}
                                >
                                    <input
                                        className="input input-bordered input-sm"
                                        value={form.nameFallback ?? ''}
                                        maxLength={60}
                                        onChange={e => set('nameFallback', e.target.value.replace(/[\r\n\t]/g, ' '))}
                                        placeholder="Ex.: tudo bem"
                                    />
                                </Field>
                                <Field
                                    label="Ou template alternativo (opcional)"
                                    hint="Usado só se o texto no lugar do nome estiver vazio."
                                >
                                    <select
                                        className="select select-bordered select-sm"
                                        value={form.fallbackTemplateId ?? ''}
                                        disabled={started || !!nameFallbackText}
                                        onChange={e => set('fallbackTemplateId', e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">Nenhum</option>
                                        {templates
                                            .filter(t => t.id !== form.templateId && !(t.variables?.length ?? 0))
                                            .map(t => (
                                                <option key={t.id} value={t.id}>
                                                    {templateLabel(t)}
                                                </option>
                                            ))}
                                    </select>
                                </Field>
                            </div>
                            {noNamePreview && (
                                <div>
                                    <div className="text-xs font-medium text-base-content/70 mb-1.5">
                                        Como chega para quem não tem nome
                                    </div>
                                    <div className="rounded-lg bg-base-200 p-3 text-sm whitespace-pre-wrap">
                                        {noNamePreview}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {started && (
                        <p className="text-xs text-base-content/60">
                            Setor e templates não mudam depois que o disparo começou. O texto no lugar do nome
                            pode mudar e vale para quem ainda não recebeu.
                        </p>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-5 space-y-5">
                    <h2 className="font-semibold">Ritmo de envio</h2>
                    {form.departmentId > 0 && <MetaLimitCard departmentId={form.departmentId} />}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field
                            label="Fatia do limite da Meta (%)"
                            hint="O limite de 24h é da conta inteira e também atende follow-ups e lembretes. 50% deixa metade para a operação."
                        >
                            <input
                                type="number"
                                min={1}
                                max={100}
                                className="input input-bordered input-sm"
                                value={form.limitSharePercent ?? 50}
                                onChange={e => set('limitSharePercent', Number(e.target.value))}
                            />
                        </Field>
                        <Field label="Teto por 24h (opcional)" hint="Vazio: só a fatia do limite. Use para começar devagar.">
                            <input
                                type="number"
                                min={1}
                                className="input input-bordered input-sm"
                                value={form.dailyCap ?? ''}
                                onChange={e => set('dailyCap', e.target.value ? Number(e.target.value) : null)}
                                placeholder="Ex.: 50"
                            />
                        </Field>
                        <Field label="Envia a partir de" hint="Horário de Brasília.">
                            <select
                                className="select select-bordered select-sm"
                                value={form.windowStartHour}
                                onChange={e => set('windowStartHour', Number(e.target.value))}
                            >
                                {HOURS.slice(0, 24).map(h => (
                                    <option key={h} value={h}>
                                        {String(h).padStart(2, '0')}h
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Para de enviar às">
                            <select
                                className="select select-bordered select-sm"
                                value={form.windowEndHour}
                                onChange={e => set('windowEndHour', Number(e.target.value))}
                            >
                                {HOURS.slice(1).map(h => (
                                    <option key={h} value={h}>
                                        {String(h).padStart(2, '0')}h
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer w-fit">
                        <input
                            type="checkbox"
                            className="toggle toggle-sm toggle-secondary"
                            checked={!!form.sendOnWeekends}
                            onChange={e => set('sendOnWeekends', e.target.checked)}
                        />
                        <span className="text-sm">Enviar também aos sábados e domingos</span>
                    </label>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    className="btn btn-secondary btn-sm gap-2"
                    disabled={saving || !(isEditing ? canUpdate : canCreate)}
                    onClick={handleSubmit}
                >
                    {saving ? <span className="loading loading-spinner loading-xs" /> : <MdSave size={16} />}
                    {isEditing ? 'Salvar' : 'Criar disparo'}
                </button>
            </div>
        </div>
    )
}

export default WhatsappBroadcastFormPage
